import { Row } from 'react-table';
import memoize from 'lodash/memoize';
import { Filter } from '../DataGridFilterMenu/DataGridFilterTypes';

export type MultiChipFilterFunction = (
  cellValues: string | number | boolean | string[],
  inputValues: string[],
) => boolean;

const parseCellValueToArray = (input: string | number | boolean | string[]) => {
  const inputToArray =
    input.toString() !== ''
      ? input
          ?.toString()
          ?.split(',')
          .map((value) => value.toLowerCase().trim())
      : [];

  return inputToArray;
};
const regexAllExp = new RegExp(/(?:all)\s\w+/);

const allFilter: MultiChipFilterFunction = (cellValues, _inputValues) => {
  return regexAllExp.test(cellValues.toString().toLowerCase());
};

const containsFilter: MultiChipFilterFunction = (cellValues, inputValues) => {
  cellValues = parseCellValueToArray(cellValues);

  if (cellValues.length === 0) {
    return true;
  }

  const cellContentValues = new Set(cellValues.map((cellValue) => cellValue.toLowerCase().trim()));

  for (const inputValue of inputValues) {
    if (cellContentValues.has(inputValue.trim().toLowerCase())) {
      return true;
    }
  }
  return false;
};

const notContainsFilter: MultiChipFilterFunction = (cellValues, inputValues) => {
  cellValues = parseCellValueToArray(cellValues);

  if (cellValues.length === 0) {
    return false;
  }

  const cellContentValues = new Set(cellValues);

  for (const inputValue of inputValues) {
    if (cellContentValues.has(inputValue.trim().toLowerCase())) {
      return false;
    }
  }
  return true;
};

const memoizeResolver = (...args: any[]) => args.join('_'); // eslint-disable-line
const filtersByOperator: Record<string, MultiChipFilterFunction> = {
  contains: memoize(containsFilter, memoizeResolver),
  notContains: memoize(notContainsFilter, memoizeResolver),
  all: memoize(allFilter, memoizeResolver),
};

function runAllFiltersOnRow<T extends Record<string, unknown>>(row: Row<T>, filters: Filter[]) {
  // Find a filter that causes the row to be removed
  return !filters.find((filter) => {
    const filterFunc = filtersByOperator[filter.operator as string];
    return !filterFunc(row.values[filter.columnId as string], filter.value as string[]);
  });
}

function dataGridMultichipFilterFunc<T extends Record<string, unknown> = Record<string, unknown>>(
  rows: Row<T>[],
  columnIds: string[],
  filterValue: Filter[],
): Row<T>[] {
  const filters = filterValue.filter((filter: Filter) => {
    return !!filter?.operator && !!filter?.columnId && !!filter.value && !!filtersByOperator[filter.operator];
  });

  return rows.filter((row) => {
    return runAllFiltersOnRow(row, filters);
  });
}

export default dataGridMultichipFilterFunc;
